import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addOgrenci } from '../../../services/api/ogrenciService'; 
import { OgrenciEkleRequestModal } from '../../../types/modals';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ogrenci-tabpanel-${index}`}
      aria-labelledby={`ogrenci-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface OgrenciEkleDialogModalProps {
  open: boolean;
  onClose: () => void;
  sezon: number;
}

const siniflar = [
  'Anaokulu', '1.Sınıf', '2.Sınıf', '3.Sınıf', '4.Sınıf', 
  '5.Sınıf', '6.Sınıf', '7.Sınıf', '8.Sınıf', '9.Sınıf', 
  '10.Sınıf', '11.Sınıf', '12.Sınıf'
];

const cinsiyetler = ['ERKEK', 'KIZ'];
const yakinlikDerecesi = ['ANNE', 'BABA', 'ABLASI', 'ABİSİ', 'TEYZESİ', 'AMCASI', 'DAYISI', 'HALASİ', 'DİĞER'];

const OgrenciEkleDialogModal: React.FC<OgrenciEkleDialogModalProps> = ({ 
  open, 
  onClose, 
  sezon 
}) => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  
  // Öğrenci bilgileri
  const [no, setNo] = useState<string>('');
  const [ogrenciAdi, setOgrenciAdi] = useState<string>('');
  const [ogrenciSoyadi, setOgrenciSoyadi] = useState<string>('');
  const [sinif, setSinif] = useState<string>('');
  const [okulKodu, setOkulKodu] = useState<string>('001');
  const [geldigiOkulAdi, setGeldigiOkulAdi] = useState<string>('');
  const [cinsiyet, setCinsiyet] = useState<string>('ERKEK');
  const [ogrenciTel, setOgrenciTel] = useState<string>('');
  const [adres, setAdres] = useState<string>('');
  const [ilce, setIlce] = useState<string>('');
  const [il, setIl] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [tcKimlikNo, setTcKimlikNo] = useState<string>('');
  const [ulke, setUlke] = useState<string>('Türkiye');
  const [dogumYeri, setDogumYeri] = useState<string>('');
  const [dogumTarihi, setDogumTarihi] = useState<string>('');
  const [aciklama, setAciklama] = useState<string>('');
  
  // Veli bilgileri
  const [veliAdiSoyadi, setVeliAdiSoyadi] = useState<string>('');
  const [veliTcKimlikNo, setVeliTcKimlikNo] = useState<string>('');
  const [veliTelefon1, setVeliTelefon1] = useState<string>('');
  const [veliTelefon2, setVeliTelefon2] = useState<string>('');
  const [veliEmail, setVeliEmail] = useState<string>('');
  const [veliMeslek, setVeliMeslek] = useState<string>('');
  const [veliAdres, setVeliAdres] = useState<string>('');
  const [veliYakinlik, setVeliYakinlik] = useState<string>('');
  
  // Anne bilgileri
  const [anneAdiSoyadi, setAnneAdiSoyadi] = useState<string>('');
  const [anneTcKimlikNo, setAnneTcKimlikNo] = useState<string>('');
  const [anneTelefon1, setAnneTelefon1] = useState<string>('');
  const [anneTelefon2, setAnneTelefon2] = useState<string>('');
  const [anneMeslek, setAnneMeslek] = useState<string>('');
  
  // Baba bilgileri
  const [babaAdiSoyadi, setBabaAdiSoyadi] = useState<string>('');
  const [babaTcKimlikNo, setBabaTcKimlikNo] = useState<string>('');
  const [babaTelefon1, setBabaTelefon1] = useState<string>('');
  const [babaTelefon2, setBabaTelefon2] = useState<string>('');
  const [babaMeslek, setBabaMeslek] = useState<string>('');
  
  // Kayıt bilgileri
  const [kayitTuru, setKayitTuru] = useState<string>('YENİ KAYIT');
  
  // Tab değişikliği
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Öğrenci ekleme mutation'ı
  const addOgrenciMutation = useMutation({
    mutationFn: (newOgrenci: OgrenciEkleRequestModal) => {
      return addOgrenci(newOgrenci);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ogrenciler'] });
      resetForm();
      onClose();
    },
    retry: 0,
  });
  
  // Form resetleme
  const resetForm = () => {
    setNo('');
    setOgrenciAdi('');
    setOgrenciSoyadi('');
    setSinif('');
    setOkulKodu('001');
    setGeldigiOkulAdi('');
    setCinsiyet('ERKEK');
    setOgrenciTel('');
    setAdres('');
    setIlce('');
    setIl('');
    setEmail('');
    setTcKimlikNo('');
    setUlke('Türkiye');
    setDogumYeri('');
    setDogumTarihi('');
    setAciklama('');
    
    setVeliAdiSoyadi('');
    setVeliTcKimlikNo('');
    setVeliTelefon1('');
    setVeliTelefon2('');
    setVeliEmail('');
    setVeliMeslek('');
    setVeliAdres('');
    setVeliYakinlik('');
    
    setAnneAdiSoyadi('');
    setAnneTcKimlikNo('');
    setAnneTelefon1('');
    setAnneTelefon2('');
    setAnneMeslek('');
    
    setBabaAdiSoyadi('');
    setBabaTcKimlikNo('');
    setBabaTelefon1('');
    setBabaTelefon2('');
    setBabaMeslek('');
    
    setKayitTuru('YENİ KAYIT');
    setTabValue(0);
  };
  
  // Öğrenci ekleme
  const handleAddOgrenci = () => {
    if (!ogrenciAdi || !ogrenciSoyadi || !sinif) {
      alert('Lütfen zorunlu alanları doldurunuz (Öğrenci Adı, Soyadı ve Sınıf).');
      return;
    }
    
    const ogrenciRequest: OgrenciEkleRequestModal = {
      kurumKodu: localStorage.getItem('kurumKodu') || '',
      no,
      ogrenciAdi,
      ogrenciSoyadi,
      sinif,
      okulKodu,
      geldigiOkulAdi,
      cinsiyet,
      ogrenciTel,
      adres,
      ilce,
      il,
      email,
      tcKimlikNo,
      ulke,
      dogumYeri,
      dogumTarihi,
      aciklama,
      veliAdiSoyadi,
      veliTcKimlikNo,
      veliTelefon1,
      veliTelefon2,
      veliEmail,
      veliMeslek,
      veliAdres,
      veliYakinlik,
      anneAdiSoyadi,
      anneTcKimlikNo,
      anneTelefon1,
      anneTelefon2,
      anneMeslek,
      babaAdiSoyadi,
      babaTcKimlikNo,
      babaTelefon1,
      babaTelefon2,
      babaMeslek,
      sezon,
      kayitTuru
    };
    
    addOgrenciMutation.mutate(ogrenciRequest);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Yeni Öğrenci Ekle
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="öğrenci bilgileri sekmeleri">
          <Tab label="Öğrenci Bilgileri" />
          <Tab label="Veli Bilgileri" />
          <Tab label="Aile Bilgileri" />
        </Tabs>
      </Box>
      
      <DialogContent>
        {/* Öğrenci Bilgileri */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Öğrenci No"
                fullWidth
                value={no}
                onChange={(e) => setNo(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sınıf</InputLabel>
                <Select
                  value={sinif}
                  onChange={(e) => setSinif(e.target.value)}
                  label="Sınıf"
                  required
                >
                  {siniflar.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Öğrenci Adı"
                fullWidth
                value={ogrenciAdi}
                onChange={(e) => setOgrenciAdi(e.target.value)}
                size="small"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Öğrenci Soyadı"
                fullWidth
                value={ogrenciSoyadi}
                onChange={(e) => setOgrenciSoyadi(e.target.value)}
                size="small"
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Cinsiyet</InputLabel>
                <Select
                  value={cinsiyet}
                  onChange={(e) => setCinsiyet(e.target.value)}
                  label="Cinsiyet"
                >
                  {cinsiyetler.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="TC Kimlik No"
                fullWidth
                value={tcKimlikNo}
                onChange={(e) => setTcKimlikNo(e.target.value)}
                size="small"
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Doğum Yeri"
                fullWidth
                value={dogumYeri}
                onChange={(e) => setDogumYeri(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Doğum Tarihi"
                type="date"
                fullWidth
                value={dogumTarihi}
                onChange={(e) => setDogumTarihi(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cep Telefonu"
                fullWidth
                value={ogrenciTel}
                onChange={(e) => setOgrenciTel(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-posta"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Adres"
                fullWidth
                value={adres}
                onChange={(e) => setAdres(e.target.value)}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="İlçe"
                fullWidth
                value={ilce}
                onChange={(e) => setIlce(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="İl"
                fullWidth
                value={il}
                onChange={(e) => setIl(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ülke"
                fullWidth
                value={ulke}
                onChange={(e) => setUlke(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Okul Kodu"
                fullWidth
                value={okulKodu}
                onChange={(e) => setOkulKodu(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Geldiği Okul Adı"
                fullWidth
                value={geldigiOkulAdi}
                onChange={(e) => setGeldigiOkulAdi(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Açıklama"
                fullWidth
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Kayıt Türü</InputLabel>
                <Select
                  value={kayitTuru}
                  onChange={(e) => setKayitTuru(e.target.value)}
                  label="Kayıt Türü"
                >
                  <MenuItem value="YENİ KAYIT">YENİ KAYIT</MenuItem>
                  <MenuItem value="TRANSFER">TRANSFER</MenuItem>
                  <MenuItem value="NAKİL">NAKİL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Veli Bilgileri */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli Adı Soyadı"
                fullWidth
                value={veliAdiSoyadi}
                onChange={(e) => setVeliAdiSoyadi(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli TC Kimlik No"
                fullWidth
                value={veliTcKimlikNo}
                onChange={(e) => setVeliTcKimlikNo(e.target.value)}
                size="small"
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli Telefon 1"
                fullWidth
                value={veliTelefon1}
                onChange={(e) => setVeliTelefon1(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli Telefon 2"
                fullWidth
                value={veliTelefon2}
                onChange={(e) => setVeliTelefon2(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli E-posta"
                fullWidth
                value={veliEmail}
                onChange={(e) => setVeliEmail(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Veli Meslek"
                fullWidth
                value={veliMeslek}
                onChange={(e) => setVeliMeslek(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Yakınlık Derecesi</InputLabel>
                <Select
                  value={veliYakinlik}
                  onChange={(e) => setVeliYakinlik(e.target.value)}
                  label="Yakınlık Derecesi"
                >
                  {yakinlikDerecesi.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Veli Adres"
                fullWidth
                value={veliAdres}
                onChange={(e) => setVeliAdres(e.target.value)}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Aile Bilgileri */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="subtitle1" gutterBottom>Anne Bilgileri</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anne Adı Soyadı"
                fullWidth
                value={anneAdiSoyadi}
                onChange={(e) => setAnneAdiSoyadi(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anne TC Kimlik No"
                fullWidth
                value={anneTcKimlikNo}
                onChange={(e) => setAnneTcKimlikNo(e.target.value)}
                size="small"
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anne Telefon 1"
                fullWidth
                value={anneTelefon1}
                onChange={(e) => setAnneTelefon1(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anne Telefon 2"
                fullWidth
                value={anneTelefon2}
                onChange={(e) => setAnneTelefon2(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Anne Meslek"
                fullWidth
                value={anneMeslek}
                onChange={(e) => setAnneMeslek(e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>Baba Bilgileri</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baba Adı Soyadı"
                fullWidth
                value={babaAdiSoyadi}
                onChange={(e) => setBabaAdiSoyadi(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baba TC Kimlik No"
                fullWidth
                value={babaTcKimlikNo}
                onChange={(e) => setBabaTcKimlikNo(e.target.value)}
                size="small"
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baba Telefon 1"
                fullWidth
                value={babaTelefon1}
                onChange={(e) => setBabaTelefon1(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baba Telefon 2"
                fullWidth
                value={babaTelefon2}
                onChange={(e) => setBabaTelefon2(e.target.value)}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baba Meslek"
                fullWidth
                value={babaMeslek}
                onChange={(e) => setBabaMeslek(e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </TabPanel>
        
        {addOgrenciMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(addOgrenciMutation.error as Error).message}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={resetForm} color="inherit" disabled={addOgrenciMutation.isPending}>
          Temizle
        </Button>
        <Button onClick={onClose} color="inherit" disabled={addOgrenciMutation.isPending}>
          İptal
        </Button>
        <Button 
          onClick={handleAddOgrenci} 
          variant="contained" 
          color="primary"
          disabled={!ogrenciAdi || !ogrenciSoyadi || !sinif || addOgrenciMutation.isPending}
        >
          {addOgrenciMutation.isPending ? <CircularProgress size={24} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OgrenciEkleDialogModal;